/**
 * 技能管理 API 服务
 * 全局技能库 CRUD + 项目级技能分配
 * 调用 axiom-agent-admin 服务（/api/v1/skills/*）
 *
 * 飞书文档：「Skill 技能管理功能 — 前端开发说明」第八节
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { SuccessResponse } from '@/shared/types/api'
import type {
  SkillListResponse,
  SkillDetail,
  CreateSkillRequest,
  SkillFileResponse,
  ProjectSkillManifest,
  UpdateProjectSkillsRequest,
} from '@/modules/agent/types/skill'

/**
 * 技能管理 API 服务
 */
export class SkillApiService {

  // ====== 技能库 API（4 个接口） ======

  /**
   * 列出技能库
   * GET /api/v1/skills/library
   */
  static async getSkillLibrary(): Promise<SuccessResponse<SkillListResponse>> {
    return ApiClient.getWithClient<SkillListResponse>(
      apiClients.agentAdmin,
      '/skills/library'
    )
  }

  /**
   * 获取技能详情
   * GET /api/v1/skills/library/{skill_name}
   */
  static async getSkillDetail(skillName: string): Promise<SuccessResponse<SkillDetail>> {
    return ApiClient.getWithClient<SkillDetail>(
      apiClients.agentAdmin,
      `/skills/library/${encodeURIComponent(skillName)}`
    )
  }

  /**
   * 创建或更新技能
   * PUT /api/v1/skills/library/{skill_name}
   * 注意：请求体中 name 必须与 URL 中的 skill_name 一致
   */
  static async createOrUpdateSkill(
    skillName: string,
    data: CreateSkillRequest
  ): Promise<SuccessResponse<SkillDetail>> {
    return ApiClient.putWithClient<SkillDetail>(
      apiClients.agentAdmin,
      `/skills/library/${encodeURIComponent(skillName)}`,
      data
    )
  }

  /**
   * 删除技能
   * DELETE /api/v1/skills/library/{skill_name}
   */
  static async deleteSkill(
    skillName: string
  ): Promise<SuccessResponse<{ deleted: boolean; skill_name: string }>> {
    return ApiClient.deleteWithClient<{ deleted: boolean; skill_name: string }>(
      apiClients.agentAdmin,
      `/skills/library/${encodeURIComponent(skillName)}`
    )
  }

  // ====== 技能脚本文件 API（2 个接口） ======

  /**
   * 上传脚本文件
   * PUT /api/v1/skills/library/{skill_name}/files/{filename}
   * Content-Type: text/plain，Body 为文件内容纯文本
   */
  static async uploadSkillFile(
    skillName: string,
    filename: string,
    content: string
  ): Promise<SuccessResponse<{ uploaded: boolean; skill_name: string; filename: string }>> {
    return ApiClient.putWithClient<{ uploaded: boolean; skill_name: string; filename: string }>(
      apiClients.agentAdmin,
      `/skills/library/${encodeURIComponent(skillName)}/files/${encodeURIComponent(filename)}`,
      content,
      { headers: { 'Content-Type': 'text/plain' } }
    )
  }

  /**
   * 读取脚本文件
   * GET /api/v1/skills/library/{skill_name}/files/{filename}
   */
  static async getSkillFile(
    skillName: string,
    filename: string
  ): Promise<SuccessResponse<SkillFileResponse>> {
    return ApiClient.getWithClient<SkillFileResponse>(
      apiClients.agentAdmin,
      `/skills/library/${encodeURIComponent(skillName)}/files/${encodeURIComponent(filename)}`
    )
  }

  // ====== 项目技能分配 API（2 个接口） ======

  /**
   * 获取项目技能清单
   * GET /api/v1/skills/projects/{project_id}
   * 如果项目还没有分配过技能，返回空列表
   */
  static async getProjectSkills(
    projectId: string
  ): Promise<SuccessResponse<ProjectSkillManifest>> {
    return ApiClient.getWithClient<ProjectSkillManifest>(
      apiClients.agentAdmin,
      `/skills/projects/${encodeURIComponent(projectId)}`
    )
  }

  /**
   * 更新项目技能清单
   * PUT /api/v1/skills/projects/{project_id}
   */
  static async updateProjectSkills(
    projectId: string,
    data: UpdateProjectSkillsRequest
  ): Promise<SuccessResponse<ProjectSkillManifest>> {
    return ApiClient.putWithClient<ProjectSkillManifest>(
      apiClients.agentAdmin,
      `/skills/projects/${encodeURIComponent(projectId)}`,
      data
    )
  }

  // ====== HermesAgent ClawHub API ======
  //
  // Search is done directly from the frontend (CORS open on clawhub.ai).
  // Only install/delete/list go through our backend (S3 operations).

  /** 列出已安装的 ClawHub 技能（全局共享） */
  static async getInstalledClawhub(): Promise<SuccessResponse<any>> {
    return ApiClient.getWithClient<any>(
      apiClients.agentAdmin,
      '/skills/clawhub/installed'
    )
  }

  /** 从 ClawHub 安装技能 */
  static async installFromClawhub(skillName: string): Promise<SuccessResponse<any>> {
    return ApiClient.postWithClient<any>(
      apiClients.agentAdmin,
      `/skills/clawhub/install?skill_name=${encodeURIComponent(skillName)}`
    )
  }

  /** 删除 ClawHub 技能 */
  static async deleteFromClawhub(skillName: string): Promise<SuccessResponse<any>> {
    return ApiClient.deleteWithClient<any>(
      apiClients.agentAdmin,
      `/skills/clawhub/${encodeURIComponent(skillName)}`
    )
  }
}

// ── Direct ClawHub API (frontend → clawhub.ai, CORS open) ──────

const CLAWHUB_BASE = "https://clawhub.ai/api/v1"

export function searchClawHubSkills(query = "", limit = 20): Promise<any[]> {
  const url = query
    ? `${CLAWHUB_BASE}/search?q=${encodeURIComponent(query)}&limit=${limit}`
    : `${CLAWHUB_BASE}/skills?limit=${limit}`
  return fetch(url)
    .then(r => r.json())
    .then(d => {
      const items = d.results || d.items || d.skills || []
      return items.map((s: any) => ({
        name: s.slug || s.name || s.id || "?",
        description: s.summary || s.description || "",
        tags: s.topics || s.tags || [],
        version: s.version || (s.tags?.latest ? String(s.tags.latest) : "") || "",
        downloads: s.downloads ?? s.stats?.downloads ?? 0,
        source: "clawhub",
      }))
    })
    .catch(() => [])
}
